/* ================================================================
   NewsPage.tsx — Actualités (V4)
   Charge les actualités PUBLIEES depuis l'API admin
   (/api/v1/content/public/news) + fallback statique.
   Bilingue FR/EN | Images résolues via l'URL du backend
   ================================================================ */

import { useState, useMemo, useEffect } from 'react'
import {
  Newspaper, Calendar, ArrowRight, X, FileText, ExternalLink,
  Download, ChevronRight, Search, Filter, BookOpen, GraduationCap,
  Award, FileSpreadsheet, Landmark, Clock, AlertTriangle, MapPin,
  Users, XCircle, SlidersHorizontal, Sparkles, Mail, Megaphone,
  CheckCircle2, Loader2,
} from 'lucide-react'
import { usePageVisit, recordPageVisit } from '../../hooks/usePageVisit'
import { useLang } from '../../hooks/useLang'

const API_BASE = (import.meta as any).env?.VITE_API_URL || '/api/v1'

/* ---------- Résolution des images : /uploads/... → http://localhost:8001/uploads/... ---------- */
const API_ORIGIN = ((import.meta.env.VITE_API_URL as string | undefined) || '').replace(/\/api\/v1\/?$/, '')
  || 'http://localhost:8001'

const resolveImg = (url?: string): string => {
  if (!url) return ''
  if (url.startsWith('http') || url.startsWith('data:')) return url
  return `${API_ORIGIN}${url.startsWith('/') ? '' : '/'}${url}`
}

/* ---------- Types API ---------- */
interface ApiNews {
  id: number
  title_fr?: string
  title_en?: string
  content_fr?: string
  content_en?: string
  summary_fr?: string
  summary_en?: string
  image_url?: string
  category?: string
  status?: string
  is_featured?: boolean
  view_count?: number
  like_count?: number
  published_at?: string
  created_at?: string
}

interface DisplayNews {
  id: number
  type: string
  typeEn: string
  badgeColor: string
  date: string        // "15 mai 2026" (affichage)
  sortKey: string     // ISO pour le tri
  title: string
  titleEn: string
  resume: string
  resumeEn: string
  detail: string
  detailEn: string
  link: string
  docLabel: string
  docLabelEn: string
  docUrl: string
  image: string
  is_featured: boolean
}

const T = {
  fr: {
    pageSubtitle: 'Toutes les informations officielles de votre établissement',
    searchPlaceholder: 'Rechercher par titre, catégorie ou contenu...',
    filtersBtn: 'Filtres',
    filterCategory: 'Filtrer par catégorie',
    recentFirst: 'Plus récent d\'abord',
    oldestFirst: 'Plus ancien d\'abord',
    resultsFound: 'résultat(s) trouvé(s)',
    reset: 'Réinitialiser',
    resetSearch: 'Réinitialiser la recherche',
    noNewsFound: 'Aucune actualité trouvée',
    noNewsDesc: 'Aucun résultat ne correspond à votre recherche',
    seeMore: 'Lire la suite',
    featured: 'À la une',
    all: 'Tous',
    loading: 'Chargement des actualités...',
    loadError: 'Impossible de charger les actualités pour le moment.',
    retry: 'Réessayer',
    newsletterTitle: 'Restez informé',
    newsletterDesc: 'Recevez les communiqués et calendriers officiels directement par email.',
    newsletterPlaceholder: 'Votre adresse email',
    newsletterBtn: 'S\'abonner',
    newsletterSuccess: 'Merci ! Vous serez informé des prochaines actualités.',
    views: 'vues',
  },
  en: {
    pageSubtitle: 'All official information from your school',
    searchPlaceholder: 'Search by title, category or content...',
    filtersBtn: 'Filters',
    filterCategory: 'Filter by category',
    recentFirst: 'Most recent first',
    oldestFirst: 'Oldest first',
    resultsFound: 'result(s) found',
    reset: 'Reset',
    resetSearch: 'Reset search',
    noNewsFound: 'No news found',
    noNewsDesc: 'No results match your search',
    seeMore: 'Read more',
    featured: 'Featured',
    all: 'All',
    loading: 'Loading news...',
    loadError: 'Unable to load news at the moment.',
    retry: 'Retry',
    newsletterTitle: 'Stay informed',
    newsletterDesc: 'Receive official communiqués and calendars directly by email.',
    newsletterPlaceholder: 'Your email address',
    newsletterBtn: 'Subscribe',
    newsletterSuccess: 'Thank you! You will be notified of upcoming news.',
    views: 'views',
  },
}

const FR_MONTHS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet',
  'août', 'septembre', 'octobre', 'novembre', 'décembre']
const EN_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December']

function formatDate(iso: string | undefined, lang: 'fr' | 'en'): { display: string; sortKey: string } {
  if (!iso) return { display: '', sortKey: '' }
  const d = new Date(iso)
  if (isNaN(d.getTime())) return { display: iso, sortKey: iso }
  const months = lang === 'fr' ? FR_MONTHS : EN_MONTHS
  return {
    display: `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`,
    sortKey: d.toISOString(),
  }
}

const CATEGORY_LABELS: Record<string, { fr: string; en: string }> = {
  education: { fr: 'Éducation', en: 'Education' },
  exam: { fr: 'Examens', en: 'Exams' },
  official: { fr: 'Communiqué officiel', en: 'Official' },
  event: { fr: 'Événements', en: 'Events' },
  results: { fr: 'Résultats', en: 'Results' },
  policy: { fr: 'Administration', en: 'Administration' },
}

const badgeFor = (cat?: string): string => {
  const c = (cat || '').toLowerCase()
  if (c === 'official' || c.includes('rentrée') || c.includes('rentree')) return 'badge-gold'
  if (c === 'results' || c.includes('sport') || c.includes('fenasco')) return 'badge-green'
  if (c === 'event' || c.includes('culture') || c.includes('calendrier')) return 'badge-purple'
  if (c === 'policy' || c.includes('sécurité') || c.includes('securite')) return 'badge-orange'
  if (c.includes('urgence')) return 'badge-red'
  if (c === 'exam' || c === 'education' || c.includes('acad')) return 'badge-blue'
  if (c.includes('urgence') || c.includes('sécurité') || c.includes('securite')) return 'badge-red'
  if (c.includes('sport') || c.includes('fenasco')) return 'badge-green'
  if (c.includes('exam') || c.includes('acad')) return 'badge-blue'
  if (c.includes('communiqué') || c.includes('communique') || c.includes('rentrée') || c.includes('rentree')) return 'badge-gold'
  if (c.includes('calendrier')) return 'badge-purple'
  if (c.includes('culture')) return 'badge-orange'
  return 'badge-blue'
}

const FALLBACK_IMG =
  'data:image/svg+xml,' + encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="800" height="400"><rect width="800" height="400" fill="#1e3a5f"/><text x="50%" y="50%" fill="#d4a017" font-family="sans-serif" font-size="28" font-weight="bold" text-anchor="middle">MINESEC</text></svg>`
  )

const handleImgError = (e: React.SyntheticEvent<HTMLImageElement>) => {
  e.currentTarget.src = FALLBACK_IMG
}

export default function NewsPage() {
  const { lang } = useLang()
  const txt = T[lang as keyof typeof T] ?? T.fr

  usePageVisit('/news', 'public')

  const [apiNews, setApiNews] = useState<ApiNews[]>([])
  const [apiLoaded, setApiLoaded] = useState(false)
  const [apiError, setApiError] = useState(false)
  const [selectedNews, setSelectedNews] = useState<DisplayNews | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [filterType, setFilterType] = useState<string>(txt.all)
  const [sortOrder, setSortOrder] = useState<'recent' | 'ancien'>('recent')
  const [showFilters, setShowFilters] = useState(false)
  const [email, setEmail] = useState('')
  const [subscribed, setSubscribed] = useState(false)

  /* ── Chargement des actualités publiées depuis l'API ── */
  useEffect(() => {
    fetch(`${API_BASE}/content/public/news?page=1&per_page=60`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => {
        setApiNews(d?.items || [])
        setApiLoaded(true)
      })
      .catch(() => {
        setApiError(true)
        setApiLoaded(true)
      })
  }, [])

  /* ── Mapping API → affichage ── */
  const items: DisplayNews[] = useMemo(() => {
    return apiNews.map((n) => {
      const { display, sortKey } = formatDate(n.published_at || n.created_at, lang)
      const contentFr = n.content_fr || ''
      const contentEn = n.content_en || n.content_fr || ''
      return {
        id: n.id,
        type: CATEGORY_LABELS[(n.category || '').toLowerCase()]?.[lang]
          ?? (n.category || (lang === 'fr' ? 'Actualité' : 'News')),
        typeEn: CATEGORY_LABELS[(n.category || '').toLowerCase()]?.en
          ?? n.category ?? 'News',
        badgeColor: badgeFor(n.category),
        date: display,
        sortKey,
        title: n.title_fr || '',
        titleEn: n.title_en || n.title_fr || '',
        resume: n.summary_fr || contentFr.slice(0, 200) + (contentFr.length > 200 ? '…' : ''),
        resumeEn: n.summary_en || n.summary_fr || contentEn.slice(0, 200) + (contentEn.length > 200 ? '…' : ''),
        detail: contentFr,
        detailEn: contentEn,
        link: '',
        docLabel: n.category || 'Document',
        docLabelEn: n.category || 'Document',
        docUrl: '',
        image: resolveImg(n.image_url),
        is_featured: !!n.is_featured,
      }
    })
  }, [apiNews, lang])

  // Reset filtre si langue change
  useEffect(() => { setFilterType(txt.all) }, [lang]) // eslint-disable-line react-hooks/exhaustive-deps

  const types = useMemo(
    () => [txt.all, ...Array.from(new Set(items.map((n) => (lang === 'fr' ? n.type : n.typeEn))))],
    [items, lang, txt.all]
  )

  const filteredNews = useMemo(() => {
    let result = [...items]
    if (filterType !== txt.all) {
      result = result.filter((n) => (lang === 'fr' ? n.type : n.typeEn) === filterType)
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      result = result.filter((n) =>
        [n.title, n.titleEn, n.resume, n.resumeEn, n.detail, n.type, n.typeEn]
          .some((f) => f.toLowerCase().includes(q))
      )
    }
    result.sort((a, b) =>
      sortOrder === 'recent' ? b.sortKey.localeCompare(a.sortKey) : a.sortKey.localeCompare(b.sortKey)
    )
    return result
  }, [items, searchQuery, filterType, sortOrder, lang, txt.all])

  const clearSearch = () => { setSearchQuery(''); setFilterType(txt.all); setSortOrder('recent') }

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.includes('@')) return
    setSubscribed(true)
  }

  const featured = filteredNews.find((n) => n.is_featured) || filteredNews[0]
  const gridNews = filteredNews.filter((n) => n.id !== featured?.id)

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* ═══════════ HERO ═══════════ */}
      <section className="relative bg-school-blue text-white pt-28 lg:pt-32 pb-14 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-school-gold/10 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-5">
            <Megaphone className="w-4 h-4 text-school-gold" />
            <span className="text-school-gold text-xs font-bold uppercase tracking-wider">
              {lang === 'fr' ? 'Actualités' : 'News'}
            </span>
          </span>
          <h1 className="text-3xl lg:text-5xl font-black mb-4 flex items-center justify-center gap-3">
            <Newspaper className="h-10 w-10 text-school-gold" />
            {lang === 'fr' ? 'Actualités' : 'News'}
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto">{txt.pageSubtitle}</p>
          <div className="relative max-w-xl mx-auto mt-8">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={txt.searchPlaceholder}
              className="w-full pl-12 pr-4 py-3.5 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-school-gold shadow-lg"
            />
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 py-10">
        {/* ═══════════ ÉTATS DE CHARGEMENT / ERREUR / VIDE ═══════════ */}
        {!apiLoaded ? (
          <div className="flex flex-col items-center justify-center py-24 bg-white rounded-2xl border border-gray-100">
            <Loader2 className="w-10 h-10 animate-spin text-school-blue mb-4" />
            <p className="text-gray-500">{txt.loading}</p>
          </div>
        ) : apiError ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-gray-100">
            <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">{txt.loadError}</p>
            <button onClick={() => window.location.reload()}
              className="bg-school-blue text-white px-5 py-2.5 rounded-lg font-semibold hover:opacity-90">
              {txt.retry}
            </button>
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-gray-100">
            <Newspaper className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">
              {lang === 'fr' ? 'Aucune actualité publiée pour le moment.' : 'No news published yet.'}
            </p>
          </div>
        ) : (
          <>
            {/* ═══════════ FILTRES ═══════════ */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-8">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex flex-wrap gap-2">
                  {types.slice(0, 7).map((tp) => (
                    <button key={tp} onClick={() => setFilterType(tp)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        filterType === tp ? 'bg-school-blue text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}>
                      {tp}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setShowFilters(!showFilters)}
                    className={`px-4 py-2 rounded-lg font-medium text-sm flex items-center gap-2 transition-all ${
                      showFilters ? 'bg-school-blue text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}>
                    <SlidersHorizontal className="h-4 w-4" />
                    {txt.filtersBtn}
                  </button>
                  <select value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value as 'recent' | 'ancien')}
                    className="px-3 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm font-medium focus:outline-none">
                    <option value="recent">{txt.recentFirst}</option>
                    <option value="ancien">{txt.oldestFirst}</option>
                  </select>
                </div>
              </div>

              {showFilters && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1.5">
                    <Filter className="h-4 w-4 text-school-blue" />
                    {txt.filterCategory}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {types.map((tp) => (
                      <button key={tp} onClick={() => setFilterType(tp)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                          filterType === tp ? 'bg-school-blue text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}>
                        {tp}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {(searchQuery || filterType !== txt.all) && (
                <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
                  <p className="text-sm text-gray-600">
                    <span className="font-semibold text-school-blue">{filteredNews.length}</span> {txt.resultsFound}
                    {searchQuery && <span> « <span className="font-medium">{searchQuery}</span> »</span>}
                  </p>
                  <button onClick={clearSearch}
                    className="text-sm text-school-blue hover:text-school-gold font-medium flex items-center gap-1 transition-colors">
                    <XCircle className="h-4 w-4" />
                    {txt.reset}
                  </button>
                </div>
              )}
            </div>

            {/* ═══════════ AUCUN RÉSULTAT (recherche) ═══════════ */}
            {filteredNews.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="h-10 h-10 w-10 text-gray-300" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{txt.noNewsFound}</h3>
                <p className="text-gray-500 mb-4">{txt.noNewsDesc} « {searchQuery} »</p>
                <button onClick={clearSearch}
                  className="bg-school-blue text-white hover:opacity-90 px-5 py-2.5 rounded-lg font-semibold transition-all">
                  {txt.resetSearch}
                </button>
              </div>
            ) : (
              <>
                {/* À LA UNE */}
                {featured && !searchQuery && (
                  <div className="relative bg-school-blue rounded-2xl overflow-hidden mb-8 cursor-pointer group shadow-xl"
                    onClick={() => setSelectedNews(featured)}>
                    <div className="grid lg:grid-cols-2">
                      <div className="relative h-64 lg:h-auto min-h-[280px] bg-school-blue/20">
                        {featured.image && (
                          <img src={featured.image} alt={lang === 'fr' ? featured.title : featured.titleEn}
                            onError={handleImgError}
                            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        )}
                      </div>
                      <div className="p-8 lg:p-10 text-white flex flex-col justify-center">
                        <div className="flex items-center gap-3 mb-4">
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-school-gold text-school-blue text-xs font-bold rounded-full">
                            <Sparkles className="w-3.5 h-3.5" />
                            {txt.featured}
                          </span>
                          <span className={`${featured.badgeColor} text-xs`}>
                            {lang === 'fr' ? featured.type : featured.typeEn}
                          </span>
                        </div>
                        <h2 className="text-2xl lg:text-3xl font-black mb-4 leading-snug">
                          {lang === 'fr' ? featured.title : featured.titleEn}
                        </h2>
                        <p className="text-blue-100/90 mb-6 line-clamp-3">
                          {lang === 'fr' ? featured.resume : featured.resumeEn}
                        </p>
                        <div className="flex items-center gap-4 text-sm text-blue-200/70">
                          <span className="flex items-center gap-1.5">
                            <Calendar className="h-4 w-4" /> {featured.date}
                          </span>
                          <span className="inline-flex items-center gap-1.5 text-school-gold font-semibold group-hover:gap-3 transition-all">
                            {txt.seeMore} <ArrowRight className="h-4 w-4" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* GRILLE */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {(searchQuery ? filteredNews : gridNews).map((news) => {
                    const displayTitle = lang === 'fr' ? news.title : news.titleEn
                    const displayResume = lang === 'fr' ? news.resume : news.resumeEn
                    const displayType = lang === 'fr' ? news.type : news.typeEn
                    return (
                      <div key={news.id}
                        className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-xl hover:-translate-y-1 hover:border-school-gold/50 transition-all duration-300 cursor-pointer"
                        onClick={() => setSelectedNews(news)}>
                        <div className="h-44 relative overflow-hidden bg-school-blue/5">
                          {news.image && (
                            <img src={news.image} alt={displayTitle}
                              onError={handleImgError}
                              className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                          )}
                          <span className={`absolute top-3 left-3 ${news.badgeColor} text-xs shadow`}>
                            {displayType}
                          </span>
                        </div>
                        <div className="p-5">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs text-gray-400 flex items-center gap-1">
                              <Calendar className="h-3 w-3" /> {news.date}
                            </span>
                          </div>
                          <h3 className="font-bold text-gray-900 mb-2 line-clamp-2 leading-snug">
                            {displayTitle}
                          </h3>
                          <p className="text-gray-500 text-sm mb-4 line-clamp-3">{displayResume}</p>
                          <span className="inline-flex items-center gap-1.5 text-school-blue hover:text-school-gold text-sm font-semibold transition-colors">
                            {txt.seeMore} <ChevronRight className="h-4 w-4" />
                          </span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </>
            )}
          </>
        )}

        {/* ═══════════ NEWSLETTER ═══════════ */}
        <div className="mt-14 bg-school-blue rounded-2xl p-8 lg:p-10 text-white relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-10 -right-10 w-64 h-64 bg-school-gold/10 rounded-full blur-3xl" />
          </div>
          <div className="relative max-w-2xl mx-auto text-center">
            <Mail className="h-10 w-10 text-school-gold mx-auto mb-4" />
            <h2 className="text-2xl lg:text-3xl font-black mb-2">{txt.newsletterTitle}</h2>
            <p className="text-blue-100 mb-6">{txt.newsletterDesc}</p>
            {subscribed ? (
              <p className="inline-flex items-center gap-2 px-5 py-3 bg-green-500/20 border border-green-400/40 text-green-300 rounded-xl font-semibold">
                <CheckCircle2 className="w-5 h-5" /> {txt.newsletterSuccess}
              </p>
            ) : (
              <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <input type="email" required value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={txt.newsletterPlaceholder}
                  className="flex-1 px-4 py-3 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-school-gold" />
                <button type="submit"
                  className="px-6 py-3 bg-school-gold text-school-blue font-bold rounded-xl hover:opacity-90 transition-all">
                  {txt.newsletterBtn}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* ═══════════ MODAL DÉTAIL ═══════════ */}
      {selectedNews && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setSelectedNews(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="relative h-48 bg-gray-100 rounded-t-2xl overflow-hidden">
              {selectedNews.image && (
                <img src={selectedNews.image}
                  alt={lang === 'fr' ? selectedNews.title : selectedNews.titleEn}
                  onError={handleImgError}
                  className="w-full h-full object-cover" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <span className={`absolute top-4 left-4 ${selectedNews.badgeColor} text-xs`}>
                {lang === 'fr' ? selectedNews.type : selectedNews.typeEn}
              </span>
              <button onClick={() => setSelectedNews(null)}
                className="absolute top-4 right-4 p-2 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/40 transition-colors">
                <X className="h-5 w-5 text-white" />
              </button>
              <div className="absolute bottom-4 left-4 right-4">
                <h2 className="text-white font-bold text-lg leading-snug drop-shadow-lg">
                  {lang === 'fr' ? selectedNews.title : selectedNews.titleEn}
                </h2>
              </div>
            </div>

            <div className="p-6">
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-4 pb-4 border-b border-gray-100">
                <span className="flex items-center gap-1.5">
                  <Calendar className="h-4 w-4 text-school-gold" /> {selectedNews.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <Landmark className="h-4 w-4 text-school-gold" /> {selectedNews.type}
                </span>
              </div>

              <p className="text-gray-700 leading-relaxed text-sm mb-6 whitespace-pre-wrap">
                {lang === 'fr' ? selectedNews.detail : selectedNews.detailEn}
              </p>

              {(selectedNews.link || selectedNews.docUrl) && (
                <div className="space-y-3">
                  {selectedNews.link && (
                    <a href={selectedNews.link} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 bg-school-blue/5 rounded-lg border border-school-blue/10 hover:bg-school-blue/10 transition-colors group">
                      <div className="p-2 bg-school-blue/10 rounded-lg group-hover:bg-school-blue/20 transition-colors">
                        <ExternalLink className="h-5 w-5 text-school-blue" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-school-blue">
                          {lang === 'fr' ? 'Lien officiel' : 'Official link'}
                        </p>
                        <p className="text-xs text-gray-500 truncate">{selectedNews.link}</p>
                      </div>
                      <ArrowRight className="h-4 w-4 text-gray-400 group-hover:text-school-blue transition-colors" />
                    </a>
                  )}
                  {selectedNews.docUrl && (
                    <a href={selectedNews.docUrl} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 bg-school-gold/5 rounded-lg border border-school-gold/10 hover:bg-school-gold/10 transition-colors group">
                      <div className="p-2 bg-school-gold/10 rounded-lg group-hover:bg-school-gold/20 transition-colors">
                        <Download className="h-5 w-5 text-school-gold" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-school-gold">
                          {lang === 'fr' ? 'Document' : 'Document'}
                        </p>
                        <p className="text-xs text-gray-500 truncate">{selectedNews.docLabel} — PDF</p>
                      </div>
                      <ArrowRight className="h-4 w-4 text-gray-400 group-hover:text-school-gold transition-colors" />
                    </a>
                  )}
                </div>
              )}
            </div>

            <div className="px-6 py-4 bg-gray-50 rounded-b-2xl border-t border-gray-100 flex items-center justify-between gap-4">
              <p className="text-xs text-gray-500">
                {lang === 'fr' ? 'Publié par l\'administration' : 'Published by administration'}
              </p>
              <button onClick={() => setSelectedNews(null)}
                className="px-4 py-2 bg-school-blue text-white text-sm rounded-lg hover:opacity-90 transition-colors shrink-0">
                {lang === 'fr' ? 'Fermer' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}